import { Close as CloseIcon, Search as SearchIcon } from '@mui/icons-material';
import { Box, IconButton, InputAdornment, TextField, Theme, Typography, debounce } from '@mui/material';
import { ChangeEvent, FC, PropsWithChildren, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { additionalFiltersStyle, containerStyle, headerStyle, searchFieldStyle } from './TableSubHeader.style';
import { TableSubHeaderProps } from './TableSubHeader.types';

export const TableSubHeader: FC<PropsWithChildren<TableSubHeaderProps>> = ({
  filter,
  selectedCount = 0,
  children,
  dense = false,
  placeholder,
  additionalFilters,
}) => {
  const { t } = useTranslation();
  const [searchText, setSearchText] = useState<string>(filter?.filterText ?? '');

  const clearFilterText = () => setSearchText('');

  const onChangeSearchTextHandler = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value);
  };

  const debounceUpdateFilter = useMemo(
    () => debounce((value: string) => filter?.setFilterText && filter.setFilterText(value), 300),
    [filter],
  );

  useEffect(() => {
    debounceUpdateFilter(searchText);
  }, [searchText, debounceUpdateFilter]);

  return (
    <Box
      display="flex"
      flexDirection="row"
      justifyContent="flex-start"
      alignItems="baseline"
      sx={(theme: Theme) => headerStyle(theme, selectedCount)}
      data-cy="tableSubHeader"
    >
      {selectedCount === 0 ? (
        <Box {...containerStyle}>
          {filter && (
            <TextField
              placeholder={placeholder ?? t('table.filter.placeholder')}
              fullWidth={false}
              size="small"
              data-cy="table-filter-input"
              sx={searchFieldStyle}
              value={searchText}
              inputProps={{
                'data-cy': 'tableSubHeader_filter',
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: filter?.filterText && (
                  <InputAdornment position="end" key="end">
                    <IconButton onClick={clearFilterText} data-cy="tableSubHeader_clearFilter">
                      <CloseIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              onChange={onChangeSearchTextHandler}
            />
          )}
          {additionalFilters && <Box sx={additionalFiltersStyle}>{additionalFilters}</Box>}
        </Box>
      ) : (
        <Box {...containerStyle}>
          <Typography variant={dense ? 'p12Medium' : 'p16Medium'} width="100%">
            {`${selectedCount} ${t('table.selected_row', { count: selectedCount })}`}
          </Typography>
          <Box display="flex" alignItems="inherit" width="100%" justifyContent="end">
            {children}
          </Box>
        </Box>
      )}
    </Box>
  );
};
